<template>
	<div v-if="deviceManufacturerForm" class="py-8">
		<DeviceManufacturerPropertiesForm
			:form="deviceManufacturerForm"
			:disabled="isReadOnly"
			@on-save="saveDeviceManufacturer"
			@on-delete="confirmDelete"
		/>
	</div>
</template>

<script>
import DeviceManufacturerPropertiesForm from "@/Forms/DeviceManufacturer/DeviceManufacturerPropertiesForm.vue";
import { mapStores } from "pinia";
import { useDeviceManufacturersStore } from "@/stores/deviceManufacturers.js";

export default {
	name: "DeviceManufacturerPropertiesView",
	components: {DeviceManufacturerPropertiesForm},
	data() {
		return {
			deviceManufacturerForm: undefined,
			readOnly: false,
		}
	},
	emits: [
		'on-save',
		'on-delete',
	],
	props: {
		manufacturer: {
			type: Object,
		},
	},
	computed: {
		...mapStores(useDeviceManufacturersStore),
		isReadOnly() {
			return this.readOnly;
		},
	},
	methods: {
		asReadOnlyForm(data) {
			return {
				...data,
				errors: {},
				processing: false,
				validate: () => {},
			};
		},
		async loadDeviceManufacturerForm(manufacturerId) {
			this.deviceManufacturerForm = undefined;
			this.readOnly = false;

			if (!manufacturerId) {
				return;
			}

			if (manufacturerId === 'NEW') {
				this.deviceManufacturerForm = await this.deviceManufacturersStore.createDeviceManufacturer();
				return;
			}

			const deviceManufacturer = this.manufacturer ?? await this.deviceManufacturersStore.showDeviceManufacturer(manufacturerId);
			if (this.$route.params.manufacturer !== manufacturerId) {
				return;
			}

			if (this.isGlobalManufacturer(deviceManufacturer) && !this.$auth.hasPermission('update:all-device-manufacturers')) {
				this.readOnly = true;
				this.deviceManufacturerForm = this.asReadOnlyForm(deviceManufacturer);
				return;
			}

			this.deviceManufacturerForm = await this.deviceManufacturersStore.editDeviceManufacturer(manufacturerId);
			if (this.$route.params.manufacturer !== manufacturerId) {
				return;
			}
			this.deviceManufacturerForm.id = manufacturerId;
		},
		async saveDeviceManufacturer() {
			try {
				const response = await this.deviceManufacturerForm.submit();
				this.$toast.add({
					severity: 'success',
					summary: this.$t('pages.admin.devices.device_manufacturers.device_manufacturer.properties.device_manufacturer_properties_view.device_manufacturer_saved'),
					life: 3000,
				});
				this.$emit('on-save');
				this.$router.replace({params: {...this.$route.params, manufacturer: response.data.id}});
			} catch (error) {
				//
			}
		},
		confirmDelete() {
			this.$confirm.require({
				group: 'delete',
				message: this.$t('pages.admin.devices.device_manufacturers.device_manufacturer.properties.device_manufacturer_properties_view.are_you_sure_you_want_to_delete_this_device_manufacturer'),
				header: this.$t('pages.admin.devices.device_manufacturers.device_manufacturer.properties.device_manufacturer_properties_view.delete_device_manufacturer'),
				accept: () => {
					this.deleteDeviceManufacturer();
				},
				reject: () => {
					this.$toast.add({
            severity: 'info',
            summary: this.$t('common.cancelled'),
            detail: this.$t('pages.admin.devices.device_manufacturers.device_manufacturer.properties.device_manufacturer_properties_view.device_manufacturer_was_not_deleted'),
            life: 3000
          });
				}
			});
		},
		async deleteDeviceManufacturer() {
			try {
				if (this.$route.params.manufacturer !== 'NEW') {
					await this.deviceManufacturersStore.deleteDeviceManufacturer(this.$route.params.manufacturer);
					this.$emit('on-delete');
				}
				this.$router.push({name: 'admin.devices.manufacturers', params: {...this.$route.params, manufacturer: undefined}});
			} catch (error) {
				console.log(error);
			}
		},
		isGlobalManufacturer(deviceManufacturer) {
			return deviceManufacturer?.company_id == null
				&& !deviceManufacturer?.company_ids?.length
				&& !deviceManufacturer?.companies?.length;
		},
	},
	watch: {
		'$route.params.manufacturer': {
			async handler(newValue, oldValue) {
				await this.loadDeviceManufacturerForm(newValue);
			},
			immediate: true,
		}
	},
}
</script>

<style scoped>

</style>
